import { makeStyles, Theme } from '@material-ui/core/styles'

export const usePhoneStyles = makeStyles((theme: Theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        backgroundColor: theme.palette.background.default,
    },
    pageHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        gap: theme.spacing(2),
        width: '100%',
        boxSizing: 'border-box',
    },
    headerLeft: {
        minWidth: 0,
        flex: 1,
    },
    headerRight: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        justifyContent: 'flex-end',
        gap: theme.spacing(0.75),
        minWidth: 0,
        flexShrink: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
    },
    titleIcon: {
        width: 36,
        height: 36,
        borderRadius: theme.spacing(1),
        backgroundColor: theme.palette.primary.main + '14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: theme.palette.primary.main,
    },
    pageSubtitle: {
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    liveChip: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
        fontWeight: 600,
        height: 24,
        '& .MuiChip-label': {
            padding: theme.spacing(0, 1),
        },
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: '50%',
        backgroundColor: '#22c55e',
        marginRight: theme.spacing(0.5),
    },
    phoneNumber: {
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
    },
    statsSection: {
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
    },
    tabsWrapper: {
        padding: theme.spacing(1.5, 3),
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: theme.palette.background.paper,
        width: '100%',
        boxSizing: 'border-box',
    },
    tabs: {
        width: '100%',
        minHeight: 'unset',
        backgroundColor: theme.palette.grey[100],
        borderRadius: theme.spacing(1),
        padding: theme.spacing(0.5),
    },
    tab: {
        textTransform: 'none',
        fontSize: '0.875rem',
        fontWeight: 500,
        minHeight: 40,
        minWidth: 0,
        maxWidth: 'none',
        flex: 1,
        borderRadius: theme.spacing(0.75),
        color: theme.palette.text.secondary,
        opacity: 1,
        transition:
            'color 0.2s ease, background-color 0.2s ease, box-shadow 0.2s ease',
        '&:hover': {
            color: theme.palette.text.primary,
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&.Mui-selected': {
            color: theme.palette.primary.main,
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main + '18',
            boxShadow: 'none',
        },
        '&.Mui-selected:hover': {
            backgroundColor: theme.palette.primary.main + '24',
        },
    },
    tabLabel: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
    },
    tabIcon: {
        fontSize: 18,
    },
    content: {
        padding: theme.spacing(3),
        width: '100%',
        boxSizing: 'border-box',
    },
    card: {
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 14,
        boxShadow: '0 2px 10px rgba(26, 45, 55, 0.07)',
    },
    cardNoPadding: {
        overflow: 'hidden',
        padding: 0,
    },
    sectionLabel: {
        fontSize: '0.75rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
    },
    greenTrend: {
        fontSize: '0.625rem',
        fontWeight: 600,
        color: '#16a34a',
    },
    kpiGrid: {
        margin: 0,
    },
    callsGrid: {
        display: 'grid',
        gap: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '420px 1fr',
        },
    },
    callListHeader: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5),
    },
    searchField: {
        '& .MuiOutlinedInput-root': {
            fontSize: '0.75rem',
        },
        '& .MuiOutlinedInput-input': {
            padding: '8px 10px',
        },
    },
    callListScroll: {
        maxHeight: 600,
        overflowY: 'auto',
    },
    callListItem: {
        display: 'flex',
        width: '100%',
        alignItems: 'flex-start',
        gap: theme.spacing(1.5),
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1.5, 2),
        textAlign: 'left',
        cursor: 'pointer',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottomWidth: 1,
        borderBottomStyle: 'solid',
        borderBottomColor: theme.palette.divider,
        transition: 'background-color 0.15s',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.03)',
        },
    },
    callListItemSelected: {
        backgroundColor: 'rgba(70, 176, 131, 0.05)',
    },
    callTypeIcon: {
        marginTop: 2,
        width: 28,
        height: 28,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
    },
    callTypeIn: {
        backgroundColor: '#dcfce7',
        color: '#15803d',
    },
    callTypeOut: {
        backgroundColor: '#dbeafe',
        color: '#1d4ed8',
    },
    callTypeMissed: {
        backgroundColor: '#fee2e2',
        color: '#b91c1c',
    },
    callListMeta: {
        minWidth: 0,
        flex: 1,
    },
    callListTime: {
        fontSize: '0.625rem',
        color: theme.palette.text.secondary,
        flexShrink: 0,
    },
    callListIntent: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
        marginTop: 2,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    },
    callListFooter: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    aiBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 2,
        fontSize: '0.625rem',
        color: theme.palette.primary.main,
    },
    forwardedLabel: {
        fontSize: '0.625rem',
        color: theme.palette.text.secondary,
    },
    detailHeader: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
    },
    detailMeta: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        fontSize: '0.75rem',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    waveform: {
        display: 'flex',
        height: 40,
        alignItems: 'flex-end',
        gap: 1,
        marginTop: theme.spacing(1.5),
    },
    waveformBar: {
        flex: 1,
        borderRadius: 2,
        backgroundColor: 'rgba(70, 176, 131, 0.3)',
    },
    chipRow: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
        marginTop: theme.spacing(1.5),
    },
    detailBody: {
        display: 'grid',
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: '1fr 280px',
        },
    },
    transcriptPanel: {
        borderRight: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2),
        [theme.breakpoints.down('sm')]: {
            borderRight: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`,
        },
    },
    transcriptHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
    },
    downloadLink: {
        fontSize: '0.6875rem',
        color: theme.palette.primary.main,
        cursor: 'pointer',
        background: 'none',
        border: 'none',
        padding: 0,
        '&:hover': {
            textDecoration: 'underline',
        },
    },
    transcriptLine: {
        display: 'flex',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
    },
    transcriptLineUser: {
        flexDirection: 'row-reverse',
    },
    transcriptAvatar: {
        width: 24,
        height: 24,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '0.625rem',
        fontWeight: 600,
        flexShrink: 0,
    },
    transcriptAvatarAi: {
        backgroundColor: 'rgba(70, 176, 131, 0.1)',
        color: theme.palette.primary.main,
    },
    transcriptAvatarUser: {
        backgroundColor: theme.palette.divider,
        color: theme.palette.text.primary,
    },
    transcriptBubble: {
        maxWidth: '80%',
        borderRadius: 8,
        padding: theme.spacing(1, 1.5),
        fontSize: '0.75rem',
        lineHeight: 1.5,
    },
    transcriptBubbleAi: {
        backgroundColor: 'rgba(70, 176, 131, 0.05)',
        color: theme.palette.text.primary,
    },
    transcriptBubbleUser: {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        color: theme.palette.text.primary,
    },
    summaryPanel: {
        padding: theme.spacing(2),
    },
    summaryText: {
        fontSize: '0.75rem',
        marginTop: theme.spacing(1),
    },
    fineTuneButton: {
        display: 'flex',
        width: '100%',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(1, 1.25),
        textAlign: 'left',
        fontSize: '0.6875rem',
        fontWeight: 500,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        color: theme.palette.text.primary,
        marginBottom: theme.spacing(1),
        '&:hover': {
            borderColor: theme.palette.primary.main,
        },
    },
    agentGrid: {
        display: 'grid',
        gap: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    cardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        fontSize: '0.875rem',
        fontWeight: 600,
    },
    cardTitleIcon: {
        color: theme.palette.primary.main,
        fontSize: 18,
    },
    voiceOption: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.5),
        cursor: 'pointer',
        marginBottom: theme.spacing(1),
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
            borderColor: 'rgba(70, 176, 131, 0.5)',
        },
    },
    voiceOptionSelected: {
        borderColor: theme.palette.primary.main,
        backgroundColor: 'rgba(70, 176, 131, 0.05)',
    },
    voiceDesc: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
    },
    fieldLabel: {
        display: 'block',
        fontSize: '0.6875rem',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(0.5),
    },
    behaviorGrid: {
        display: 'grid',
        gap: theme.spacing(1.5),
        marginTop: theme.spacing(1.5),
        [theme.breakpoints.up('md')]: {
            gridTemplateColumns: 'repeat(3, 1fr)',
        },
    },
    behaviorItem: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.5),
    },
    behaviorLabel: {
        fontSize: '0.6875rem',
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        color: theme.palette.text.secondary,
    },
    behaviorValue: {
        fontSize: '0.875rem',
        fontWeight: 600,
        marginTop: theme.spacing(0.5),
    },
    knowledgeGrid: {
        display: 'grid',
        gap: theme.spacing(2),
        [theme.breakpoints.up('lg')]: {
            gridTemplateColumns: '1fr 1fr',
        },
    },
    docRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1),
    },
    docMeta: {
        fontSize: '0.6875rem',
        color: theme.palette.text.secondary,
    },
    faqItem: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 6,
        padding: theme.spacing(1.5),
        marginBottom: theme.spacing(1),
    },
    routingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1, 1.5),
        marginBottom: theme.spacing(1),
    },
    routingAddRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1px dashed ${theme.palette.divider}`,
        borderRadius: 8,
        padding: theme.spacing(1.5),
        marginTop: theme.spacing(2),
    },
    setupRoot: {
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100%',
        width: '100%',
        alignItems: 'center',
        justifyContent: 'center',
        padding: theme.spacing(8, 3),
        boxSizing: 'border-box',
    },
    setupCard: {
        textAlign: 'center',
        padding: theme.spacing(4),
        maxWidth: 672,
        width: '100%',
    },
    setupIcon: {
        fontSize: 40,
        color: theme.palette.primary.main,
    },
    outcomeChip: {
        borderRadius: 999,
        padding: '2px 8px',
        fontSize: '0.625rem',
        fontWeight: 600,
        display: 'inline-block',
    },
    outcomePositive: {
        backgroundColor: '#ecfdf5',
        color: '#15803d',
    },
    outcomeNeutral: {
        backgroundColor: '#f3f4f6',
        color: '#374151',
    },
    outcomeNegative: {
        backgroundColor: '#fef2f2',
        color: '#b91c1c',
    },
    outcomeBrand: {
        backgroundColor: 'rgba(70, 176, 131, 0.1)',
        color: theme.palette.primary.main,
    },
}))
